// Olivium Governance — durable canonical read helpers (pure, testable).
//
// Bridges the REAL trust sources (olv_audit_trail via useAuditFeed,
// olv_verify_event_chain via useChainIntegrity) into the governance ledger.
// The status derivation is deliberately conservative: demo data is NEVER
// labeled durable. Only a live runtime with an authenticated session yields
// mode: 'durable'.

import type { DurableStatus, GovRecord, Severity, StatusKind } from './governanceModel'

/** Minimal shape of an olv_audit_trail row (subset of the hook's AuditEvent). */
export interface AuditEventLike {
  id?: string
  sequence?: number
  action?: string
  event_type?: string
  actor_id?: string
  actor_role?: string
  resource_type?: string
  resource_id?: string
  outcome?: string
  severity?: string
  chain_hash?: string
  created_at?: string
}

export interface DurableProbe {
  live: boolean
  auditAuthRequired: boolean
  chainAuthRequired: boolean
  /** True when the LIVE olv_audit_trail read failed (missing table / query error). */
  auditError: boolean
  auditCount: number
  chainValid: boolean
  chainMessage: string
  missingRpc: boolean
}

/**
 * Derive the honest durable-source status. Demo mode is 'local' (never durable);
 * a live runtime without a session is 'auth_required'; a live runtime whose
 * audit read FAILED is 'unavailable' (a failed read is surfaced, never read as
 * a successful durable read of zero events); only a live, authed, successful
 * read is 'durable'.
 */
export function deriveDurableStatus(p: DurableProbe): DurableStatus {
  let mode: DurableStatus['mode']
  if (!p.live) mode = 'local'
  else if (p.auditAuthRequired || p.chainAuthRequired) mode = 'auth_required'
  else if (p.auditError) mode = 'unavailable'
  else mode = 'durable'
  return {
    mode,
    // Only assert a count when the read actually succeeded (mode 'durable').
    auditCount: mode === 'durable' ? p.auditCount : 0,
    chainValid: mode === 'durable' ? p.chainValid : null,
    chainMessage: p.chainMessage,
    missingRpc: p.missingRpc,
  }
}

const SEVERITY_MAP: Record<string, Severity> = {
  critical: 'critical', warning: 'medium', info: 'info', low: 'low', high: 'high',
}

function severityOf(raw?: string): Severity {
  return (raw && SEVERITY_MAP[raw]) || 'info'
}

function statusKindOf(outcome?: string): StatusKind {
  if (outcome === 'denied' || outcome === 'blocked') return 'denied'
  return 'identity'
}

/** Map a real olv_audit_trail row to a durable GovRecord (labeled saved_durable). */
export function auditEventToRecord(ev: AuditEventLike, index: number): GovRecord {
  const seq = ev.sequence ?? index
  const id = `dur_evt_${seq}`
  const at = ev.created_at ?? new Date(0).toISOString()
  const action = ev.action ?? ev.event_type ?? 'event'
  const outcome = ev.outcome ?? 'success'
  return {
    id,
    module: 'audit',
    correlationId: ev.id ?? `dur_corr_${seq}`,
    title: action.replace(/_/g, ' '),
    summary: `${action} · ${outcome}${ev.resource_type ? ` on ${ev.resource_type}` : ''}`,
    status: outcome === 'denied' || outcome === 'blocked' ? 'Denied' : 'Recorded',
    statusKind: statusKindOf(outcome),
    severity: severityOf(ev.severity),
    actor: ev.actor_id ?? 'system',
    actorRole: 'Operator',
    createdAt: at,
    updatedAt: at,
    origin: 'durable',
    savedStatus: 'saved_durable',
    fields: {
      result: outcome,
      actor_role: ev.actor_role ?? 'system',
      resource: `${ev.resource_type ?? ''}${ev.resource_id ? `/${ev.resource_id}` : ''}`,
      chain_hash: ev.chain_hash ?? '',
      sequence: String(seq),
    },
    labels: [], assignee: null, reviewers: [], watchers: [], dueDate: null,
    comments: [], updates: [],
    history: [{ id: `dur_hist_${seq}`, actor: ev.actor_id ?? 'system', action: 'ingested', at, savedStatus: 'saved_durable' }],
    links: [],
  }
}

/** A short human line describing the durable status for the honesty banner. */
export function durableStatusLine(status: DurableStatus | null): string {
  if (!status) return 'Probing durable sources…'
  switch (status.mode) {
    case 'durable':
      return `Durable read active — ${status.auditCount} events from olv_audit_trail. Integrity chain: ${status.missingRpc ? 'verification service pending' : status.chainValid ? 'verified' : 'INVALID'}.`
    case 'auth_required':
      return 'Live runtime — sign in for durable reads. Showing the local-session ledger.'
    case 'unavailable':
      return 'Durable sources unavailable — showing the local-session ledger.'
    case 'local':
    default:
      return 'Demo / local mode — this is the honestly-labeled local-session ledger, not durable data.'
  }
}
